import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { billingService, queryKeys } from '@/services';
import type { ManualAdjustmentListParams } from '@/services/billing/billing-service';

/**
 * List manual adjustments (LR-563), scoped to a counterparty via `filters`.
 * Disabled until one is picked (`null`), like useBillingPreview — the endpoint
 * is unscoped without it and would return every counterparty's adjustments.
 * keepPreviousData so switching counterparty doesn't blank the list.
 */
export const useManualAdjustments = (
  filters: ManualAdjustmentListParams | null
) => {
  const cacheKey = filters
    ? Object.fromEntries(
        Object.entries(filters)
          .filter(([, v]) => v !== undefined && v !== '')
          .map(([k, v]) => [k, String(v)])
      )
    : undefined;

  return useQuery({
    queryKey: queryKeys.billing.adjustmentList(cacheKey),
    queryFn: () => billingService.getAdjustments(filters ?? undefined),
    enabled: filters !== null && Number.isFinite(filters.counterparty),
    placeholderData: keepPreviousData,
    // A short-lived, hand-edited list: a stale cache on reopen would show a row
    // someone else already posted, and the actions here (edit/delete/post) all
    // branch on that state.
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};
